import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  Monitor, 
  Smartphone, 
  RefreshCw, 
  AlertCircle,
  Download,
  Eye
} from 'lucide-react';
import { ApiService } from '../utils/api';
import { useToast } from './Toast';

const LivePreviewPanel = ({ eventId, badgeConfig, isLoading = false }) => {
  const [previewBlob, setPreviewBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [previewMode, setPreviewMode] = useState('desktop'); // 'desktop', 'mobile'
  
  const { showError } = useToast();

  // Generate preview when badgeConfig changes
  useEffect(() => {
    if (badgeConfig && !isLoading) {
      generatePreview();
    }
  }, [badgeConfig, isLoading]);

  // Cleanup blob URL when component unmounts or preview changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const generatePreview = async () => {
    if (!badgeConfig || !eventId) return;

    setIsPreviewLoading(true);
    setPreviewError(null);

    try {
      const blob = await ApiService.previewBadgeConfig(eventId, badgeConfig);
      
      // Cleanup previous URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      
      // Create new blob URL
      const newPreviewUrl = URL.createObjectURL(blob);
      setPreviewBlob(blob);
      setPreviewUrl(newPreviewUrl);
    } catch (error) {
      console.error('Preview generation failed:', error);
      setPreviewError(error.message || 'Failed to generate preview');
      showError('Failed to generate badge preview. Please try again.');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleDownloadPreview = () => {
    if (!previewBlob) return;

    const url = URL.createObjectURL(previewBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `badge-preview-${eventId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleRefreshPreview = () => {
    generatePreview();
  };

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <Eye className="w-5 h-5" />
            <span>Live Preview</span>
          </CardTitle>
          <div className="flex items-center space-x-2">
            {/* Preview Mode Toggle */}
            <div className="flex items-center space-x-1 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setPreviewMode('desktop')}
                className={`p-2 rounded-md transition-colors ${
                  previewMode === 'desktop'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Desktop Preview"
              >
                <Monitor className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPreviewMode('mobile')}
                className={`p-2 rounded-md transition-colors ${
                  previewMode === 'mobile'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Mobile Preview"
              >
                <Smartphone className="w-4 h-4" />
              </button>
            </div>
            
            {/* Action Buttons */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefreshPreview}
              disabled={isPreviewLoading}
            >
              <RefreshCw className={`w-4 h-4 ${isPreviewLoading ? 'animate-spin' : ''}`} />
            </Button>
            
            {previewBlob && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadPreview}
              >
                <Download className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-4">
        <div className={`preview-container ${previewMode === 'mobile' ? 'mobile-preview' : 'desktop-preview'}`}>
          {isPreviewLoading ? (
            <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <div className="text-center">
                <RefreshCw className="w-8 h-8 text-gray-400 mx-auto mb-2 animate-spin" />
                <p className="text-gray-600">Generating preview...</p>
              </div>
            </div>
          ) : previewError ? (
            <div className="flex items-center justify-center h-96 bg-red-50 rounded-lg border-2 border-dashed border-red-300">
              <div className="text-center">
                <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-red-600 mb-2">Preview Error</p>
                <p className="text-sm text-red-500 mb-4">{previewError}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefreshPreview}
                  className="text-red-600 border-red-600 hover:bg-red-50"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry
                </Button>
              </div>
            </div>
          ) : previewUrl ? (
            <div className="preview-frame-container">
              <iframe
                src={previewUrl}
                className={`preview-frame ${
                  previewMode === 'mobile' 
                    ? 'w-full h-96 max-w-sm mx-auto' 
                    : 'w-full h-96'
                } border rounded-lg shadow-sm`}
                title="Badge Preview"
                style={{
                  backgroundColor: 'white',
                  minHeight: '400px'
                }}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <div className="text-center">
                <Eye className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600">Configure badge settings to see preview</p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
      
      <style jsx>{`
        .preview-container {
          transition: all 0.3s ease;
        }
        
        .mobile-preview {
          max-width: 400px;
          margin: 0 auto;
        }
        
        .desktop-preview {
          width: 100%;
        }
        
        .preview-frame {
          transition: all 0.3s ease;
          border: 1px solid #e5e7eb;
        }
        
        .preview-frame-container {
          position: relative;
          background: #f9fafb;
          padding: 1rem;
          border-radius: 0.5rem;
        }
      `}</style>
    </Card>
  );
};

export default LivePreviewPanel;
